import { useState } from "react";
import { classAccent } from "@/lib/brand";
import { ProgressBar } from "@/components/ProgressBar";
import { ArrowRight, Eye } from "lucide-react";

const SAMPLE_PROGRESS = 40;

function HomeCard({ cls, accent, days }) {
  const done = Math.round((days * SAMPLE_PROGRESS) / 100);
  return (
    <div className="rounded-3xl border-2 border-ink overflow-hidden bg-white shadow-pop flex flex-col max-w-sm" data-testid="preview-home-card">
      <div className="p-6" style={{ background: accent.bg, color: accent.fg }}>
        <div className="flex items-center gap-2 flex-wrap mb-10">
          {cls.theme && (
            <span className="text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full border-2 border-ink" style={{ background: accent.tag, color: "#0F1116" }} data-testid="preview-theme">
              {cls.theme}
            </span>
          )}
          <span className="text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full border-2 border-ink" style={{ background: "rgba(255,255,255,0.22)", color: accent.fg }}>
            {days} days
          </span>
        </div>
        <h3 className="text-2xl font-extrabold leading-tight" data-testid="preview-title">{cls.title || "Untitled class"}</h3>
      </div>
      <div className="p-6">
        <p className="font-reading text-sm text-muted-foreground mb-4 line-clamp-3">{cls.description}</p>
        <div className="flex justify-between text-xs font-bold mb-1">
          <span>{done}/{days}</span><span>{SAMPLE_PROGRESS}%</span>
        </div>
        <ProgressBar value={SAMPLE_PROGRESS} color={accent.bg} />
        <div className="w-full h-11 mt-4 rounded-full border-2 border-ink font-bold grid place-items-center text-sm" style={{ background: "#0F1116", color: "#FFFDF5" }}>
          <span className="flex items-center gap-1">Continue <ArrowRight size={16} /></span>
        </div>
      </div>
    </div>
  );
}

function ClassHeader({ cls, accent, days }) {
  const done = Math.round((days * SAMPLE_PROGRESS) / 100);
  return (
    <div className="rounded-3xl border-2 border-ink p-6 shadow-pop" style={{ background: accent.bg, color: accent.fg }} data-testid="preview-class-header">
      {cls.theme && (
        <span className="inline-block text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full border-2 border-ink mb-3" style={{ background: accent.tag, color: "#0F1116" }}>
          {cls.theme}
        </span>
      )}
      <h1 className="text-3xl font-extrabold">{cls.title || "Untitled class"}</h1>
      <p className="font-reading opacity-90 mt-1 mb-4">{cls.description}</p>
      <div className="flex justify-between text-xs font-bold mb-1">
        <span>{done}/{days} days</span><span>{SAMPLE_PROGRESS}%</span>
      </div>
      <ProgressBar value={SAMPLE_PROGRESS} color="#0F1116" />
    </div>
  );
}

export function ClassPreview({ cls, dirty }) {
  const [view, setView] = useState("home");
  const accent = classAccent(cls.slug, 0, cls.color);
  const days = (cls.weeks || []).reduce((n, w) => n + (w.days?.length || 0), 0);
  const Btn = ({ k, label }) => (
    <button type="button" onClick={() => setView(k)} data-testid={`preview-view-${k}`}
      className={`px-3 py-1.5 rounded-full border-2 border-ink text-xs font-bold press ${view === k ? "" : "bg-white"}`}
      style={view === k ? { background: "#0F1116", color: "#FFFDF5" } : {}}>
      {label}
    </button>
  );
  return (
    <div className="rounded-3xl border-2 border-ink p-6 lg:sticky lg:top-24 self-start" style={{ background: "#FFFDF5" }} data-testid="class-preview">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <h3 className="font-extrabold text-lg flex items-center gap-2"><Eye size={18} /> Student preview</h3>
        {dirty && <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full border-2 border-ink" style={{ background: "#FFD93D" }} data-testid="preview-unsaved">Unsaved changes</span>}
      </div>
      <div className="flex gap-2 mb-4">
        <Btn k="home" label="Home card" />
        <Btn k="class" label="Class page" />
      </div>
      {view === "home" ? <HomeCard cls={cls} accent={accent} days={days} /> : <ClassHeader cls={cls} accent={accent} days={days} />}
      <p className="text-xs text-muted-foreground mt-4">Sample progress shown. Students see this after you save.</p>
    </div>
  );
}
